import type { PanelPhase } from 'components/collapsePanel/CollapsePanel';
import { ISubscription } from 'pages/subscriptions/models';
import { isBeforeExpiryDate } from './renewalDates';

/** What a renewal panel needs to know about each subscription it covers (one per subject). */
export type RenewalSubjectInfo = Pick<
  ISubscription,
  'subjectsId' | 'schoolAccountPackagesId' | 'currentPackagesId' | 'renewalPackagesId' | 'bRenewable'
>;

/**
 * Subscriptions that share an auto-renew setting. Each group is one options call,
 * one basket and one payment, so the multi-subject discount only survives inside a combined group.
 */
export type RenewalGroup = {
  /** Stable identity: auto-renew value plus the sorted subject ids. */
  key: string;
  autoRenew: boolean;
  subjects: RenewalSubjectInfo[];
  /** Index in the subscriptions list of the group's last card; the panel renders after it. */
  lastCardIndex: number;
};

export const groupKey = (autoRenew: boolean, subjectIds: number[]): string =>
  (autoRenew ? 'on' : 'off') + ':' + [...subjectIds].sort((a, b) => a - b).join('-');

/**
 * Past its renewal date and outside the renewable period that follows it, so it can only be
 * bought again through Add subscription. bRenewable carries the API's period, so this never
 * disagrees with bCanAddSubscription.
 */
export const isLapsed = (sub: ISubscription, today: Date = new Date()): boolean =>
  !sub.bRenewable && isBeforeExpiryDate(sub.renewalDate, today);

/**
 * A renewal can be changed from any point before its date until the renewable period after it
 * ends. bRenewable alone is narrower: it is the payment window.
 */
const hasRenewalPanel = (sub: ISubscription, today: Date): boolean =>
  sub.bRenewable || (!!sub.renewalDate && !isBeforeExpiryDate(sub.renewalDate, today));

/**
 * Groups the subscriptions by bAutoRenew, keeping card order. A subject appearing more than once
 * keeps one row, because the panel keys defaults and saves by subject: its first renewable row,
 * so it stays payable, else its first row.
 */
export const groupRenewalSubjects = (list: ISubscription[], today: Date = new Date()): RenewalGroup[] => {
  const rowBySubject = new Map<number, number>();
  list.forEach((sub, index) => {
    if (!hasRenewalPanel(sub, today)) {
      return;
    }
    const held = rowBySubject.get(sub.subjectsId);
    if (held === undefined || (!list[held].bRenewable && sub.bRenewable)) {
      rowBySubject.set(sub.subjectsId, index);
    }
  });

  const byAutoRenew = new Map<boolean, { subjects: RenewalSubjectInfo[]; lastCardIndex: number }>();

  list.forEach((sub, index) => {
    if (rowBySubject.get(sub.subjectsId) !== index) {
      return;
    }

    const autoRenew = !!sub.bAutoRenew;
    const group = byAutoRenew.get(autoRenew) || { subjects: [], lastCardIndex: index };
    group.subjects.push({
      subjectsId: sub.subjectsId,
      schoolAccountPackagesId: sub.schoolAccountPackagesId,
      currentPackagesId: sub.currentPackagesId,
      renewalPackagesId: sub.renewalPackagesId,
      bRenewable: !!sub.bRenewable,
    });
    group.lastCardIndex = index;
    byAutoRenew.set(autoRenew, group);
  });

  return Array.from(byAutoRenew.entries())
    .map(([autoRenew, group]) => ({
      key: groupKey(autoRenew, group.subjects.map((s) => s.subjectsId)),
      autoRenew,
      subjects: group.subjects,
      lastCardIndex: group.lastCardIndex,
    }))
    .sort((a, b) => a.lastCardIndex - b.lastCardIndex);
};

/** The group whose panel covers this subscription row, if any. */
export const groupForSubscription = (groups: RenewalGroup[], sub: ISubscription): RenewalGroup | undefined =>
  groups.find((g) => g.subjects.some((s) => s.schoolAccountPackagesId === sub.schoolAccountPackagesId));

/* ---- regroup: outgoing panels collapse while incoming ones expand, so the page below shifts once, by the difference ---- */

export type RenderedGroup = {
  group: RenewalGroup;
  phase: PanelPhase;
};

/**
 * Reconciles the panels on screen with the groups derived from the list. Entering panels are
 * ordered before leaving ones so a new panel takes the slot while the old collapses beneath it.
 * A leaving panel wanted again turns back into entering; the first render animates nothing.
 */
export const reconcileRenderedGroups = (prev: RenderedGroup[], target: RenewalGroup[]): RenderedGroup[] => {
  const animateEnter = prev.length > 0;
  const targetKeys = target.map((g) => g.key);
  const prevKeys = prev.map((r) => r.group.key);

  const kept = prev
    .filter((r) => targetKeys.indexOf(r.group.key) !== -1)
    .map((r): RenderedGroup => ({
      group: target.find((g) => g.key === r.group.key) || r.group,
      phase: r.phase === 'leaving' ? 'entering' : r.phase,
    }));

  const entering = target
    .filter((g) => prevKeys.indexOf(g.key) === -1)
    .map((g): RenderedGroup => ({ group: g, phase: animateEnter ? 'entering' : 'stable' }));

  const leaving = prev
    .filter((r) => targetKeys.indexOf(r.group.key) === -1)
    .map((r): RenderedGroup => ({ ...r, phase: 'leaving' }));

  return [...kept, ...entering, ...leaving];
};

/** Drops a panel that has finished collapsing. */
export const finishLeaving = (prev: RenderedGroup[], key: string): RenderedGroup[] =>
  prev.filter((r) => !(r.group.key === key && r.phase === 'leaving'));

/** Panels that render after the card at `index`; a leaving panel whose card is gone renders after the last card. */
export const panelsAfterCard = (rendered: RenderedGroup[], index: number, cardCount: number): RenderedGroup[] =>
  rendered.filter(
    (r) => r.group.lastCardIndex === index || (index === cardCount - 1 && r.group.lastCardIndex >= cardCount)
  );
